import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import CodeMirror from '@uiw/react-codemirror';
import { json, jsonParseLinter } from '@codemirror/lang-json';
import { linter, lintGutter } from '@codemirror/lint';
import { EditorView } from '@codemirror/view';
import { githubDark, githubLight } from '@uiw/codemirror-theme-github';

const source = document.getElementById('apiRequestBody');
const mount = document.getElementById('apiRequestBodyEditor');

const formatJson = (value) => {
  if (!String(value || '').trim()) return '';
  return JSON.stringify(JSON.parse(value), null, 2);
};

const getJsonState = (value) => {
  if (!String(value || '').trim()) {
    return {
      label: 'Sem body',
      tone: 'muted',
      detail: '0 linhas'
    };
  }

  try {
    const formatted = formatJson(value);
    const lines = formatted.split('\n').length;
    return {
      label: 'JSON valido',
      tone: 'ok',
      detail: `${lines} linha${lines === 1 ? '' : 's'}`
    };
  } catch (error) {
    return {
      label: 'JSON inválido',
      tone: 'error',
      detail: error.message
    };
  }
};

function JsonBodyEditor() {
  const [value, setValue] = useState(source?.value || '');
  const [theme, setTheme] = useState(document.documentElement.dataset.theme || 'light');
  const jsonState = useMemo(() => getJsonState(value), [value]);

  const syncSource = useCallback((nextValue) => {
    if (!source) return;
    source.value = nextValue;
    source.dispatchEvent(new Event('input', { bubbles: true }));
  }, []);

  const updateValue = useCallback((nextValue) => {
    setValue(nextValue);
    syncSource(nextValue);
  }, [syncSource]);

  const extensions = useMemo(() => [
    json(),
    lintGutter(),
    linter(jsonParseLinter(), { delay: 180 }),
    EditorView.lineWrapping,
    EditorView.theme({
      '&': {
        height: '100%',
        minHeight: '100%',
        background: 'transparent'
      },
      '.cm-scroller': {
        fontFamily: '"JetBrains Mono", "SFMono-Regular", Consolas, monospace',
        fontSize: '12.4px',
        lineHeight: '1.68'
      },
      '.cm-content': {
        minHeight: '100%',
        padding: '12px 0'
      },
      '.cm-gutters': {
        borderRight: '1px solid var(--line)',
        backgroundColor: 'var(--control-bg)',
        color: 'var(--muted)'
      },
      '.cm-activeLine, .cm-activeLineGutter': {
        backgroundColor: 'color-mix(in srgb, var(--primary-soft) 52%, transparent)'
      },
      '.cm-tooltip': {
        border: '1px solid var(--line)',
        borderRadius: '8px',
        backgroundColor: 'var(--surface)',
        color: 'var(--text)',
        boxShadow: '0 14px 34px rgba(15, 23, 42, 0.14)'
      }
    })
  ], []);

  useEffect(() => {
    const receiveBody = (event) => {
      setValue(String(event.detail?.value ?? source?.value ?? ''));
    };
    window.addEventListener('tdemand:api-body-set', receiveBody);
    return () => window.removeEventListener('tdemand:api-body-set', receiveBody);
  }, []);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setTheme(document.documentElement.dataset.theme || 'light');
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  const formatBody = () => {
    try {
      updateValue(formatJson(value));
    } catch (error) {
      window.dispatchEvent(new CustomEvent('tdemand:api-body-invalid', {
        detail: { message: error.message }
      }));
    }
  };

  const clearBody = () => updateValue('');

  return (
    <div className="td-json-editor-shell">
      <div className="td-json-editor-toolbar">
        <span className={`td-json-editor-status is-${jsonState.tone}`}>
          <span className="material-symbols-rounded" aria-hidden="true">
            {jsonState.tone === 'ok' ? 'check_circle' : jsonState.tone === 'error' ? 'error' : 'data_object'}
          </span>
          {jsonState.label}
        </span>
        <span className="td-json-editor-detail">{jsonState.detail}</span>
        <div className="td-json-editor-actions">
          <button type="button" onClick={formatBody}>Formatar</button>
          <button type="button" onClick={clearBody}>Limpar</button>
        </div>
      </div>
      <CodeMirror
        value={value}
        height="100%"
        basicSetup={{
          autocompletion: true,
          bracketMatching: true,
          closeBrackets: true,
          foldGutter: true,
          highlightActiveLine: true,
          highlightActiveLineGutter: true,
          lineNumbers: true
        }}
        extensions={extensions}
        theme={theme === 'dark' ? githubDark : githubLight}
        onChange={updateValue}
      />
    </div>
  );
}

if (source && mount) {
  createRoot(mount).render(<JsonBodyEditor />);
}
